import noop from 'lodash/noop';

import { PractitionerDetails } from '@dreryk/egabinet-personnel-frontend-api';
import { Address } from '@dreryk/address-common-api';

import {
   DoctorPersonalDataPart,
   PersonalDataBean,
   PersonalDataFormController,
   SpecialistPersonalDataPart,
} from './PersonalDataForm.types';
import { DoctorSpecialty } from './sections/DoctorSpecialtiesSection/DoctorSpecialtiesInteractiveTable';

export const isCurrentDoctor = (updatingDoctorPersonalDataAvailable: boolean): boolean => {
   return updatingDoctorPersonalDataAvailable;
};

const isCurrentParamedic = (updatingParamedicPersonalDataAvailable: boolean): boolean => {
   return updatingParamedicPersonalDataAvailable;
};

export const forDoctor = (
   controller: PersonalDataFormController<any>,
   updatingDoctorPersonalDataAvailable: boolean
): controller is PersonalDataFormController<DoctorPersonalDataPart> => {
   return isCurrentDoctor(updatingDoctorPersonalDataAvailable);
};

export const forSpecialist = (
   controller: PersonalDataFormController<any>,
   updatingDoctorPersonalDataAvailable: boolean
): controller is PersonalDataFormController<SpecialistPersonalDataPart> => {
   return !isCurrentDoctor(updatingDoctorPersonalDataAvailable);
};

export const forParamedic = (
   controller: PersonalDataFormController<any>,
   updatingParamedicPersonalDataAvailable: boolean
): controller is PersonalDataFormController<SpecialistPersonalDataPart> => {
   return isCurrentParamedic(updatingParamedicPersonalDataAvailable);
};

type PersonalDataVisitorConfig = {
   formData: PersonalDataBean<unknown>;
   updatingParamedicPersonalDataAvailable: boolean;
};

export const personalDataVisitor = (config: PersonalDataVisitorConfig) => <ACCEPT,>(visitor: {
   withDoctor?: (bean: PersonalDataBean<DoctorPersonalDataPart>) => ACCEPT;
   withSpecialist?: (bean: PersonalDataBean<SpecialistPersonalDataPart>) => ACCEPT;
}) => {
   if (config.updatingParamedicPersonalDataAvailable) {
      return visitor.withDoctor?.(config.formData as PersonalDataBean<DoctorPersonalDataPart>);
   }

   if (!config.updatingParamedicPersonalDataAvailable) {
      return visitor.withSpecialist?.(config.formData as PersonalDataBean<SpecialistPersonalDataPart>);
   }

   return noop();
};

export const getDoctorPersonalDataPart = (
   practitionerDetails: PractitionerDetails,
   doctorAddress: Address | undefined
): DoctorPersonalDataPart => {
   if (doctorAddress) {
      return {
         medicalLicenceNumber: practitionerDetails.medicalLicenceNumber ?? null,
         noSpecialty: practitionerDetails.noSpecialty,
         doctorSpecialties: practitionerDetails.doctorSpecialties,
         includeAddress: true,
         building: doctorAddress?.findBuilding() ?? null,
         flat: doctorAddress?.findFlat() ?? null,
         street: doctorAddress?.findStreet() ?? null,
         town: doctorAddress?.findTown() ?? null,
         postcode: doctorAddress?.findPostcode() ?? null,
      };
   }
   return {
      medicalLicenceNumber: practitionerDetails.medicalLicenceNumber ?? null,
      noSpecialty: practitionerDetails.noSpecialty,
      doctorSpecialties: practitionerDetails.doctorSpecialties,
      includeAddress: false,
   };
};

export const getSpecialistPersonalDataPart = (practitionerDetails: PractitionerDetails): SpecialistPersonalDataPart => {
   return {
      pesel: practitionerDetails.pesel ?? null,
      paramedicalProfession: practitionerDetails.paramedicalProfessions.at(0),
   };
};

export const withSpecialties = (
   controller: PersonalDataFormController<DoctorPersonalDataPart>
): controller is PersonalDataFormController<DoctorPersonalDataPart> => {
   return !controller.watch('noSpecialty');
};

export const getSpecialties = (
   watch: PersonalDataFormController<DoctorPersonalDataPart>['watch']
): DoctorSpecialty[] | undefined => {
   return watch('doctorSpecialties');
};
